import { useEffect, useMemo, useState } from 'react';
import raf from 'raf';
import ResizeObserver from 'resize-observer-polyfill';

type HandlerT = (measures: { width: number; height: number }, element: Element) => void;

export const useResizeObserver = (handler: HandlerT, el: HTMLElement | null) => {
  const [node, setRef] = useState(el);

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        if (!Array.isArray(entries)) return;
        if (!entries.length) return;
        const entry = entries[0];

        // execute on the next animation frame
        raf(() =>
          handler(
            {
              width: (entry.target as HTMLElement).offsetWidth,
              height: (entry.target as HTMLElement).offsetHeight,
            },
            entry.target,
          ),
        );
      }),
    [handler],
  );

  const htmlEl = el || node;

  useEffect(() => {
    if (htmlEl) resizeObserver.observe(htmlEl);
    return () => {
      if (htmlEl) resizeObserver.unobserve(htmlEl);
    };
  }, [htmlEl, resizeObserver]);

  return [setRef];
};

export default useResizeObserver;
